import React, { useEffect, useState } from "react";

function LcdRow({ text, blink }) {
  const padded = (text + "                    ").slice(0, 20);
  return (
    <div style={{ lineHeight: "1.9em", height: "1.9em", whiteSpace: "pre" }}>
      {padded}
      {blink && <span className="caret">▌</span>}
    </div>
  );
}

/* ─── Main LCD Terminal ──────────────────────────────────────────────── */
export function LcdTerminal({ telemetry, testid }) {
  const [pzmPage, setPzmPage] = useState(0);
  const [backlight, setBacklight] = useState(true);

  // Row 3 cycles L1 → L2 → L3 → Total every 5 s - mirrors pzemPage in firmware
  useEffect(() => {
    const id = setInterval(() => setPzmPage((p) => (p + 1) % 5), 5000);
    return () => clearInterval(id);
  }, []);

  /* flicker the backlight very occasionally - subtle realism */
  useEffect(() => {
    const flicker = () => {
      if (Math.random() > 0.92) {
        setBacklight(false);
        setTimeout(() => setBacklight(true), 60 + Math.random() * 40);
      }
    };
    const id = setInterval(flicker, 8000);
    return () => clearInterval(id);
  }, []);

  const pad = (s = "", n = 20) => (s + " ".repeat(n)).slice(0, n);
  const has = !!telemetry;

  let lines = [
    "   CAMPUS ENERGY",
    "   MONITOR  v2.6",
    "   HiveMQ Cloud ",
    "   awaiting data",
  ];

  if (has) {
    const t = telemetry;

    // Row 0 - mirrors firmware: "IP: x.x.x.x" or "WiFi: reconnecting.."
    const row0 = t.wifi
      ? pad("IP: " + (t.ip || "0.0.0.0"))
      : pad("WiFi: reconnecting..");

    // Row 1 - mirrors snprintf(r1,21,"Tmp:%5.1fC Fan:%3d%%  ",tempC,fanPercent)
    const tempVal = (t.temperature ?? 0).toFixed(1).padStart(5);
    const fanVal  = String(t.fan_speed ?? 0).padStart(3);
    const row1 = pad("Tmp:" + tempVal + "C Fan:" + fanVal + "%");

    // Row 2 - mirrors pirStr + "WiFi:OK " / "WiFi:-- "
    const pirStr  = t.pir  ? "PIR:Motion " : "PIR:Idle   ";
    const wifiStr = t.wifi ? "WiFi:OK "    : "WiFi:-- ";
    const row2 = pad(pirStr + wifiStr);

    // Row 3 - mirrors pzemPage rotation (L1→L2→L3→Total, 5 s each)
    const phNames = ["L1", "L2", "L3"];
    let row3;
    if (pzmPage < 3) {
      const ph = t.phases?.[phNames[pzmPage]];
      if (ph && ph.voltage > 0) {
        // mirrors snprintf(r3,21,"L%d:%5.1fV%7.0fW   ",i+1,voltage,power)
        const v = ph.voltage.toFixed(1).padStart(5);
        const w = Math.round(ph.power).toString().padStart(7);
        row3 = pad("L" + (pzmPage + 1) + ":" + v + "V" + w + "W");
      } else {
        // mirrors snprintf(r3,21,"L%d: No PZEM data   ",i+1)
        row3 = pad("L" + (pzmPage + 1) + ": No PZEM data");
      }
    } else if (pzmPage === 3) {
      // mirrors snprintf(r3,21,"Total:%8.0f W     ",total)
      const w = Math.round(t.total_power ?? 0).toString().padStart(8);
      row3 = pad("Total:" + w + " W");
    } else {
      // mirrors snprintf(r3,21,"Gas:%4.0fppm %-6s",gasPpm,gasLabel)
      const ppm     = Math.round(t.gas_ppm ?? 0).toString().padStart(4);
      const quality = (t.gas_quality ?? "----").padEnd(6).slice(0, 6);
      row3 = pad("Gas:" + ppm + "ppm " + quality);
    }

    lines = [row0, row1, row2, row3];
  }

  const glassOpacity = backlight ? 1 : 0.18;

  return (
    <div data-testid={testid} style={{ userSelect: "none" }}>

      {/* ── Outer plastic housing / bezel ── */}
      <div style={{
        background: "linear-gradient(160deg, #2a2a2a 0%, #1a1a1a 60%, #111 100%)",
        borderRadius: 10,
        padding: "10px 10px 6px 10px",
        boxShadow: "0 4px 18px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.5)",
        border: "1px solid #383838",
      }}>

        {/* Mounting screw holes - top corners */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, paddingInline: 2 }}>
          {[0, 1].map((k) => (
            <div key={k} style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "radial-gradient(circle at 35% 35%, #555, #1a1a1a)",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.8), 0 0 0 1px #444",
            }} />
          ))}
        </div>

        {/* ── LCD glass panel ── */}
        <div style={{
          position: "relative",
          background: "#061206",
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid #1a3a1a",
          boxShadow: `inset 0 0 40px rgba(0,180,60,${backlight ? 0.12 : 0.02}), 0 0 0 1px #0a2a0a`,
          transition: "box-shadow 0.06s",
          padding: "10px 12px",
          opacity: glassOpacity,
          minHeight: 88,
        }}>

          {/* Scanlines overlay - very subtle, doesn't blur text */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2,
            backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.10) 0px, rgba(0,0,0,0.10) 1px, transparent 1px, transparent 4px)",
          }} />

          {/* Backlight glow - corners bleed */}
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
            background: backlight
              ? "radial-gradient(ellipse at 50% 50%, rgba(0,200,80,0.07) 0%, transparent 70%)"
              : "none",
          }} />

          {/* Glass reflection */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: "40%", pointerEvents: "none", zIndex: 3,
            background: "linear-gradient(180deg, rgba(255,255,255,0.025) 0%, transparent 100%)",
            borderRadius: "4px 4px 0 0",
          }} />

          {/* Character rows */}
          <div style={{
            position: "relative", zIndex: 4,
            fontFamily: "'JetBrains Mono', 'Courier New', monospace",
            fontSize: 15,
            fontWeight: 700,
            color: "#00EE66",
            letterSpacing: "0.08em",
            textShadow: "0 0 2px #00FF88",
            WebkitFontSmoothing: "none",
            MozOsxFontSmoothing: "unset",
            textRendering: "optimizeSpeed",
          }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} data-testid={`${testid}-line${i + 1}`}>
                <LcdRow text={lines[i] || "                    "} blink={i === 3} />
              </div>
            ))}
          </div>
        </div>

        {/* ── I2C backpack PCB strip ── */}
        <div style={{
          marginTop: 5,
          background: "linear-gradient(90deg, #1a2f1a 0%, #0f200f 50%, #1a2f1a 100%)",
          borderRadius: "0 0 4px 4px",
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid #1f3f1f",
          borderTop: "none",
        }}>
          {/* I2C header pins */}
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            {["GND","VCC","SDA","SCL"].map((pin) => (
              <div key={pin} style={{ textAlign: "center" }}>
                <div style={{
                  width: 4, height: 10, borderRadius: 1,
                  background: "linear-gradient(180deg, #d4a940 0%, #a07820 100%)",
                  margin: "0 auto 2px",
                  boxShadow: "0 0 3px rgba(200,160,60,0.4)",
                }} />
                <div style={{ fontSize: 6, color: "#4a8a4a", fontFamily: "monospace", letterSpacing: 0 }}>{pin}</div>
              </div>
            ))}
          </div>

          {/* Chip label */}
          <div style={{
            fontSize: 7, color: "#3a6a3a", fontFamily: "monospace",
            letterSpacing: "0.05em", textAlign: "center",
          }}>
            <div>PCF8574A</div>
            <div style={{ color: "#2a5a2a" }}>I²C 0x27</div>
          </div>

          {/* Contrast potentiometer */}
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 14, height: 14, borderRadius: "50%",
              background: "radial-gradient(circle at 40% 35%, #555, #222)",
              boxShadow: "0 0 0 2px #333, 0 2px 4px rgba(0,0,0,0.5)",
              margin: "0 auto 2px",
              position: "relative",
            }}>
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                width: 1, height: 4,
                background: "#888",
                transform: "translate(-50%, -90%) rotate(30deg)",
                borderRadius: 1,
              }} />
            </div>
            <div style={{ fontSize: 6, color: "#3a6a3a", fontFamily: "monospace" }}>CONT</div>
          </div>
        </div>

        {/* Bottom mounting screw holes */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingInline: 2 }}>
          {[0, 1].map((k) => (
            <div key={k} style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "radial-gradient(circle at 35% 35%, #555, #1a1a1a)",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.8), 0 0 0 1px #444",
            }} />
          ))}
        </div>
      </div>

      {/* Label below */}
      <p className="text-xs text-[var(--t2)] font-mono mt-2">
        Mirrors the on-device 20×4 character LCD in real time.
      </p>
    </div>
  );
}
